import { t } from "i18next";
import { selector } from "recoil";

import { BookingDto, IBookingCalendarPassengerDto } from "../../dto";
import {
  calculateDateDifference,
  generateDatesArrayYMDString,
  getIsCalendarPassengerPhoneRestrictedBySearch,
  getIsCalendarPassengerWorkerCodeRestrictedBySearch,
  getSearchTokens,
  isPassengerAllowedByViewMode,
  Log,
  parseBookingCellId,
  resolveIsBookingRestrictedBySearch,
  toBookingCellId,
  unique,
} from "../../helpers";
import { getBookingCalendarPasssengerDto } from "../../helpers/passenger";
import { BookingCellId, DateYMDString } from "../../models";
import {
  bookingCalendarViewModeAtom,
  bulkApprovalBookingConflictsAtom,
  passengersListBookingAtom,
  selectedCellsBookingCalendarAtom,
  tableSearchAF,
  topCalendarDatesAtomFamily,
} from "../atoms";
import { boundedSelector, boundedSelectorFamily } from "../selector-factories";
import { bookingsSelector } from "./bookings.selectors";
import { timezoneSelector } from "./system-settings.selectors";
import { filterCriteriaSF } from "./table-filters.selectors";

export const datesArrayCalendarBookingsSF = selector<DateYMDString[]>({
  key: "datesArrayCalendarBookingsSF",
  get: ({ get }) => {
    const calendarDates = get(topCalendarDatesAtomFamily("booking"));

    if (!calendarDates) {
      return [];
    }

    const timezone = get(timezoneSelector);
    const datesArray = generateDatesArrayYMDString(
      calendarDates.date1,
      calendarDates.date2,
      timezone,
    );
    return datesArray || [];
  },
});

export const daysCountCalendarBookingsSF = selector<number>({
  key: "daysCountCalendarBookingsSF",
  get: ({ get }) => {
    const calendarDates = get(topCalendarDatesAtomFamily("booking"));

    if (!calendarDates) {
      return 0;
    }

    return calculateDateDifference(calendarDates.date1, calendarDates.date2);
  },
});

export type ColumnIndex = number;
export type RowIndex = number;
export type BookingCalendarCellIdDatePassengerMapKey =
  `${ColumnIndex},${RowIndex}`;

export const bookingCalendarCellIdDatePassengerMap = boundedSelector<
  Map<BookingCalendarCellIdDatePassengerMapKey, BookingCellId>
>({
  key: "bookingCalendarCellIdDatePassengerMap",
  get: ({ get }) => {
    const bookingDatesYYYYMMDD = get(datesArrayCalendarBookingsSF);
    const passengerIds = get(passengersBookingIdsSelector);

    return bookingDatesYYYYMMDD.reduce(
      (acc, bookingDateYYYYMMDD, columnIndex) => {
        passengerIds.forEach((passengerId, rowIndex) => {
          acc.set(
            `${columnIndex as ColumnIndex},${rowIndex as RowIndex}`,
            toBookingCellId(passengerId, bookingDateYYYYMMDD),
          );
        });
        return acc;
      },
      new Map<BookingCalendarCellIdDatePassengerMapKey, BookingCellId>(),
    );
  },
});

export const bookingCalendarCellIdDatePassengerSF = boundedSelectorFamily<
  BookingCellId,
  BookingCalendarCellIdDatePassengerMapKey
>({
  key: "bookingCalendarCellIdDatePassengerSF",
  get:
    mapKey =>
    ({ get }) => {
      const map = get(bookingCalendarCellIdDatePassengerMap);

      return map.get(mapKey) as BookingCellId;
    },
});

export const bookingCalendarRowIndexPassengerSF = boundedSelectorFamily<
  IBookingCalendarPassengerDto | null,
  number
>({
  key: "bookingCalendarRowIndexPassengerSF",
  get:
    rowIndex =>
    ({ get }) => {
      const bookingPassengers = get(visiblePassengersBookingsSelector);

      if (bookingPassengers[rowIndex] === undefined) {
        Log.error(
          t("errors.couldntGetPassengerByRowIndex", { rowIndex: rowIndex }),
        );
        return null;
      }

      return bookingPassengers[rowIndex];
    },
});

export const passengersBookingsCountSelector = boundedSelector<number>({
  key: "passengersBookingsCountSelector",
  get: ({ get }) => {
    return get(visiblePassengersBookingsSelector).length;
  },
});

export const passengersBookingIdsSelector = boundedSelector<
  IBookingCalendarPassengerDto["passengerId"][]
>({
  key: "passengersBookingIdsSelector",
  get: ({ get }) => {
    const bookingPassengers = get(visiblePassengersBookingsSelector);

    return bookingPassengers.map(
      bookingPassenger => bookingPassenger.passengerId,
    );
  },
});

export const visiblePassengersIdsBookingSelector = boundedSelector<number[]>({
  key: "visiblePassengersIdsBookingSelector",
  get: ({ get }) => {
    const passengers = get(visiblePassengersBookingsSelector);
    const passengerIds = passengers.map(passenger => passenger.passengerId);

    return passengerIds;
  },
});

export const visiblePassengersBookingsSelector = boundedSelector<
  IBookingCalendarPassengerDto[]
>({
  key: "visiblePassengersBookingsSelector",
  get: ({ get }) => {
    const passengersList = get(passengersListBookingAtom);
    const bookings = get(bookingsSelector);
    const calendarViewMode = get(bookingCalendarViewModeAtom);
    const passengers: IBookingCalendarPassengerDto[] = [];
    const searchValue = get(tableSearchAF("booking"));
    const typeOrmCriteriaObj = get(filterCriteriaSF("booking"));

    const passengerIdBookingsMap = bookings.reduce((acc, booking) => {
      const prevBookings = acc.get(booking.passengerId) ?? [];
      return acc.set(booking.passengerId, [...prevBookings, booking]);
    }, new Map<number, BookingDto[]>());

    const passengerIdFilterSet = typeOrmCriteriaObj?.passengerId?.$in
      ? new Set(typeOrmCriteriaObj.passengerId.$in as number[])
      : null;

    passengersList.forEach(passenger => {
      const passengerName =
        `${passenger.firstName} ${passenger.lastName}`.toLowerCase();

      const queryTokens = getSearchTokens(searchValue);

      const isFullNameRestrictedBySearch = queryTokens.every(token =>
        passengerName.includes(token),
      );
      const isWorkerCodeRestrictedBySearch =
        getIsCalendarPassengerWorkerCodeRestrictedBySearch({
          passenger,
          searchValue,
        });
      const isPhoneRestrictedBySearch =
        getIsCalendarPassengerPhoneRestrictedBySearch({
          passenger,
          searchValue,
        });
      const isPassengerHasBookings = passengerIdBookingsMap.has(passenger.id);
      const isBookingRestrictedBySearch = resolveIsBookingRestrictedBySearch({
        isPassengerHasBookings,
        passengerIdBookingsMap,
        passengerId: passenger.id,
        searchValue,
      });
      const isPassengerVisible =
        (passenger.isActive || isPassengerHasBookings) &&
        (isFullNameRestrictedBySearch ||
          isWorkerCodeRestrictedBySearch ||
          isBookingRestrictedBySearch ||
          isPhoneRestrictedBySearch);

      const isPassengerAllowed = isPassengerAllowedByViewMode({
        mode: calendarViewMode,
        passengerId: passenger.id,
        passengerIdBookingMap: passengerIdBookingsMap,
      });
      const isPassengerFilter = Boolean(typeOrmCriteriaObj?.passengerId);
      const isPassengerSatisfyFilter = passengerIdFilterSet
        ? passengerIdFilterSet.has(passenger.id)
        : false;

      if (isPassengerVisible && isPassengerAllowed) {
        if (isPassengerFilter) {
          if (isPassengerSatisfyFilter) {
            passengers.push(getBookingCalendarPasssengerDto(passenger));
          }
        } else {
          passengers.push(getBookingCalendarPasssengerDto(passenger));
        }
      }
    });

    return passengers.sort((a, b) => {
      const aName = a.passengerName || "";
      const bName = b.passengerName || "";
      return aName.localeCompare(bName);
    });
  },
});
export const selectedPassengersBookingsSelector = boundedSelector<
  IBookingCalendarPassengerDto[]
>({
  key: "selectedPassengersBookingsSelector",
  get: ({ get }) => {
    const passengersList = get(passengersListBookingAtom);
    const selectedCells = get(selectedCellsBookingCalendarAtom);
    const selectedPassengerIds = unique(
      [...selectedCells].map(cellId => {
        const { passengerId } = parseBookingCellId(cellId);
        return passengerId;
      }),
    );

    const selectedPassengerIdsSet = new Set(selectedPassengerIds);
    return passengersList
      .filter(passenger => selectedPassengerIdsSet.has(passenger.id))
      .map(getBookingCalendarPasssengerDto)
      .sort((a, b) => {
        const aName = a.passengerName || "";
        const bName = b.passengerName || "";
        return aName.localeCompare(bName);
      });
  },
});

export const bookingCalendarSelectedCellsCount = selector<number>({
  key: "bookingCalendarSelectedCellsCount",
  get: ({ get }) => {
    const selectedCellsIds = get(selectedCellsBookingCalendarAtom);
    return selectedCellsIds.size || 0;
  },
});

export const bookingCalendarBulkSetPreviewSuccessfullyBookedSelector =
  selector<number>({
    key: "bookingCalendarBulkSetPreviewSuccessfullyBookedSelector",
    get: ({ get }) => {
      const bookingCount = get(selectedCellsBookingCalendarAtom).size;
      const bulkApprovalBookingConflicts = get(
        bulkApprovalBookingConflictsAtom,
      );
      const bulkApprovalBookingConflictsApproved = Array.from(
        bulkApprovalBookingConflicts.values(),
      ).filter(bookingConflicts =>
        bookingConflicts.every(({ approvalReason }) => !approvalReason),
      );

      return bookingCount - bulkApprovalBookingConflictsApproved.length;
    },
  });

export const selectedBookingCalendarDatesSelector = selector<
  Set<DateYMDString>
>({
  key: "selectedBookingCalendarDates",
  get: ({ get }) => {
    const selectedBookingCellIds = get(selectedCellsBookingCalendarAtom);
    const uniqDateSet: Set<DateYMDString> = new Set();

    for (const selectedBookingCellId of selectedBookingCellIds) {
      const { date } = parseBookingCellId(selectedBookingCellId);
      uniqDateSet.add(date);
    }

    return uniqDateSet;
  },
});
